import classes from "./ManageTags.module.scss";
import React, { memo, useCallback, useState } from "react";
import { IconSecondaryButton } from "components/core/IconSecondaryButton";
import plusIcon from "components/icons/plusIcon.svg";
import AddingTag from "components/core/tags/AddingTag";
import RemovableTag from "components/core/tags/RemovableTag";
import moreIcon from "components/icons/moreIcon.svg";
import Tag from "../core/tags/Tag";

interface IProps {
  tags: string[];
  onChange: (tags: string[]) => void;
}

const TAGS_SHOW_LIMIT = 3;
const TAGS_MIN = 3;
const TAGS_MAX = 15;

export const ManageTags = memo((props: IProps) => {
  const { tags, onChange } = props;

  const [isShowMore, setShowMore] = useState(false);
  const [isAddingMode, setAddingMode] = useState(false);

  const handleShowMore = () => {
    setShowMore(prevState => !prevState);
  };

  const handleEnableAddingMode = useCallback(() => {
    setAddingMode(true);
  }, []);

  const handleDisableAddingMode = useCallback(() => {
    setAddingMode(false);
  }, []);

  const handleRemoveTag = useCallback((tag: string) => () => {
    if (tags.length <= TAGS_MIN) {
      return;
    }

    const index = tags.indexOf(tag);

    if (index === -1) {
      return;
    }

    const newTags = [...tags];
    newTags.splice(index, 1);
    onChange(newTags);
  }, [onChange, tags]);

  const handleAddedTag = useCallback((tag: string) => () => {
    if (tags.length >= TAGS_MAX) {
      return;
    }

    const newTags = [...tags];
    newTags.push(tag);
    onChange(newTags);
    handleDisableAddingMode();
  }, [onChange, tags, handleDisableAddingMode]);

  return (
    <div className={classes.manageTags}>
      {
        tags.length < TAGS_MAX ? (
          isAddingMode ? (
            <AddingTag addedTags={tags} onAdded={handleAddedTag} onClose={handleDisableAddingMode}/>
          ) : (
            <IconSecondaryButton url={plusIcon} variant="active" onClick={handleEnableAddingMode}/>
          )
        ) : null
      }

      {
        tags.map((tag, index) => {
          if ((!isShowMore && index < TAGS_SHOW_LIMIT) || isShowMore) {
            return (
              tags.length > TAGS_MIN ? (
                <RemovableTag key={tag} tag={tag} onRemove={handleRemoveTag}/>
              ) : (
                <Tag key={tag} tag={tag}/>
              )
            );
          } else {
            return null;
          }
        })
      }

      {
        tags.length > TAGS_SHOW_LIMIT ? (
          <IconSecondaryButton url={moreIcon} onClick={handleShowMore}/>
        ) : null
      }

    </div>
  );
});
